import { useEffect, useMemo, useState } from 'react';

import { type AppLocale } from '@irl/types';

export interface TimezoneNowParts {
  hours: string;
  minutes: string;
  isoTime: string;
}

function localeTag(locale: AppLocale): string {
  return locale === 'en' ? 'en-US' : 'ru-RU';
}

function readTimeParts(date: Date, timezone: string, locale: AppLocale): TimezoneNowParts | null {
  try {
    const parts = new Intl.DateTimeFormat(localeTag(locale), {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const hours = parts.find((part) => part.type === 'hour')?.value;
    const minutes = parts.find((part) => part.type === 'minute')?.value;
    if (!hours || !minutes) {
      return null;
    }

    return { hours, minutes, isoTime: `${hours}:${minutes}` };
  } catch {
    return null;
  }
}

/** Live HH:mm in IANA timezone — ticks every second for subtle clock UI. */
export function useTimezoneNowParts(
  timezone: string | undefined,
  locale: AppLocale,
): TimezoneNowParts | null {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  return useMemo(() => {
    if (!timezone) {
      return null;
    }
    return readTimeParts(now, timezone, locale);
  }, [locale, now, timezone]);
}
